import {
  createContext,
  forwardRef,
  type ComponentPropsWithoutRef,
  type HTMLAttributes,
  type ReactNode,
  useContext,
} from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../lib/utils';

const AlertContext = createContext<{ variant: 'default' | 'destructive' }>({
  variant: 'default',
});

const alertVariants = cva(
  'relative flex w-full min-w-0 max-w-full items-start gap-3 overflow-x-hidden rounded-md border p-4 text-sm shadow-2xs',
  {
    variants: {
      variant: {
        default: 'border-border bg-card text-card-foreground',
        destructive: 'border-destructive/30 bg-card text-destructive',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface AlertProps
  extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof alertVariants> {
  /** Pass `null` to hide the icon. Omit to use the default triangle icon. */
  icon?: ReactNode | null;
}

const Alert = forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant = 'default', icon, children, ...props }, ref) => (
    <AlertContext.Provider value={{ variant: variant ?? 'default' }}>
      <div ref={ref} role="alert" className={cn(alertVariants({ variant }), className)} {...props}>
        {icon !== null && (
          <div className="mt-0.5 shrink-0 [&_svg]:size-4 [&_svg]:shrink-0">
            {icon ?? <TriangleAlertIcon />}
          </div>
        )}
        <div className="flex min-w-0 flex-1 flex-col gap-1">{children}</div>
      </div>
    </AlertContext.Provider>
  )
);
Alert.displayName = 'Alert';

const AlertTitle = forwardRef<HTMLHeadingElement, ComponentPropsWithoutRef<'h5'>>(
  ({ className, ...props }, ref) => {
    const { variant } = useContext(AlertContext);
    return (
      <h5
        ref={ref}
        className={cn(
          'mb-0 font-medium leading-5 tracking-tight',
          variant === 'default' && 'text-foreground',
          variant === 'destructive' && 'text-destructive',
          className
        )}
        {...props}
      />
    );
  }
);
AlertTitle.displayName = 'AlertTitle';

const AlertDescription = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>(
  ({ className, ...props }, ref) => {
    const { variant } = useContext(AlertContext);
    return (
      <div
        ref={ref}
        className={cn(
          'text-sm leading-5 [&_p]:leading-relaxed',
          variant === 'default' && 'text-muted-foreground',
          variant === 'destructive' && 'text-destructive',
          className
        )}
        {...props}
      />
    );
  }
);
AlertDescription.displayName = 'AlertDescription';

function TriangleAlertIcon({ className, ...props }: ComponentPropsWithoutRef<'svg'>) {
  const { variant } = useContext(AlertContext);
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn(
        variant === 'default' && 'text-foreground',
        variant === 'destructive' && 'text-destructive',
        className
      )}
      aria-hidden
      {...props}
    >
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </svg>
  );
}

export { Alert, AlertDescription, AlertTitle, alertVariants };
